import { Dispatch, SetStateAction, useRef, useState } from 'react';
import { Dropdown, MenuProps } from 'antd';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { openNotification, pascalToHyphenString } from '../../../utils/helper';
import { useReactToPrint } from 'react-to-print';
import { getSingleDocuments } from '../../../api/documents/documents';
import MainPreviewComponent from './previewComponents/MainPreviewComponent';
import { hasPermission } from '../dashboardpage/utils';
import { useUserProfile } from '../../../customhooks/useUserProfile';
import CustomTable from '../../CustomTable';
import DocumentsTableMobileCard from './DocumentsTableMobileCard';
import { FilterIcon, PlusIcon, EditIcon } from '../../icons';

interface IProps {
    setShowAddModal: Dispatch<SetStateAction<boolean>>;
    setVisibleDocument: Dispatch<SetStateAction<boolean>>;
    loadingData: boolean;
    data?: any[];
    setCurrentData: Dispatch<SetStateAction<any>>;
    currentData: {
        createdType: string;
        documentType: string;
        id: number | undefined;
    };
}

const AllDocumentsTable = (props: IProps) => {
    const navigate = useNavigate();
    const printContentRef = useRef<HTMLDivElement | null | any>(null);

    const handlePrint = useReactToPrint({
        content: () => printContentRef.current
    });

    const [downloadLoading, setDownloadLoading] = useState<boolean>(false);
    const [singleData, setSingleData] = useState<any>(null);
    const [previewId, setPreviewId] = useState<any>(null);
    const { permissions } = useUserProfile();

    const fetchApiData = async () => {
        try {
            const res = await getSingleDocuments({
                corporateDocumentId: props.currentData?.id
            });
            const { data } = res.data;
            setSingleData(data?.documentData);
            setPreviewId(data?.id);
            setDownloadLoading(false);
        } catch (error) {
            setDownloadLoading(false);
            if (error?.response) {
                openNotification(
                    'error',
                    `Document Details, Something went wrong`
                );
            } else {
                openNotification(
                    'error',
                    `Documents Details, Something went wrong`
                );
            }
        }
    };

    const handlePrintDownloadClick = async () => {
        await fetchApiData();
        if (!downloadLoading) {
            handlePrint();
        }
    };

    const items: MenuProps['items'] =
        props?.currentData?.createdType === 'UPLOADED'
            ? [
                  {
                      key: '1',
                      label: (
                          <button
                              className='flex items-center h-[52px] w-[150px] gap-[16px] text-steelBlue text-base'
                              onClick={() => props.setVisibleDocument(true)}
                          >
                              <PlusIcon />
                              <span>Preview</span>
                          </button>
                      )
                  }
              ]
            : [
                  {
                      key: '0',
                      label: (
                          <button
                              className='flex items-center h-[52px] w-[150px] gap-[16px] text-steelBlue text-base'
                              onClick={() => props.setVisibleDocument(true)}
                          >
                              <PlusIcon />
                              <span>Preview</span>
                          </button>
                      )
                  },

                  {
                      key: '1',
                      label: (
                          <button
                              className='flex items-center h-[52px] w-[150px] gap-[16px] text-steelBlue text-base'
                              onClick={() => handlePrintDownloadClick()}
                          >
                              <FilterIcon />
                              <span>Download</span>
                          </button>
                      )
                  },
                  {
                      key: '2',
                      label: (
                          <button
                              className={`flex items-center h-[52px] w-[150px] gap-[16px] text-steelBlue text-base ${
                                  permissions &&
                                  !hasPermission({
                                      userPermissions: permissions,
                                      module: 'tradeDocuments',
                                      permissionList: ['write', '*']
                                  }) &&
                                  ` opacity-30 cursor-not-allowed`
                              }`}
                              onClick={() => {
                                  if (
                                      permissions &&
                                      hasPermission({
                                          userPermissions: permissions,
                                          module: 'tradeDocuments',
                                          permissionList: ['write', '*']
                                      })
                                  ) {
                                      props.currentData.documentType ===
                                      'DRAFT_BILL_OF_LADING'
                                          ? navigate(
                                                `/trade-documents/house-bill-of-lading?edit=${props?.currentData?.id}`
                                            )
                                          : props.currentData.documentType ===
                                            'DRAFT_GROSS_MASS_DECLARATION'
                                          ? navigate(
                                                `/trade-documents/verified-gross-mass-declaration?edit=${props?.currentData?.id}`
                                            )
                                          : props.currentData.documentType ===
                                            'DRAFT_CERTIFICATE_OF_ORIGIN'
                                          ? navigate(
                                                `/trade-documents/certificate-of-origin?edit=${props?.currentData?.id}`
                                            )
                                          : navigate(
                                                `/trade-documents/${pascalToHyphenString(
                                                    props?.currentData
                                                        ?.documentType
                                                )}?edit=${props?.currentData
                                                    ?.id}`
                                            );
                                  } else {
                                      openNotification(
                                          'error',
                                          'You do not have access to perform this action'
                                      );
                                  }
                              }}
                          >
                              <EditIcon />
                              <span>Edit</span>
                          </button>
                      )
                  }
              ];
    return (
        <div className='bg-appcolorwhite lg:mt-[24px]'>
            <CustomTable
                loadingData={props.loadingData}
                emptyText='You have no documents.'
                emptyActionText='Create or Upload a document to get started.'
                emptyAction={() => {
                    props.setShowAddModal(true);
                }}
                data={props?.data || []}
                columns={[
                    {
                        title: 'Last Modified',
                        dataIndex: 'lastModified',
                        key: 'lastModified',
                        render: (_, record: any) => (
                            <span>
                                {moment(
                                    parseInt(record?.lastModifiedDate)
                                ).format('ll')}
                            </span>
                        )
                    },
                    {
                        title: 'Date Added',
                        dataIndex: 'createdDate',
                        key: 'createdDate',
                        render: (_, record: any) => (
                            <span>
                                {moment(parseInt(record?.createdDate)).format(
                                    'll'
                                )}
                            </span>
                        )
                    },
                    {
                        title: 'Name Tag',
                        dataIndex: 'documentName',
                        key: 'documentName',
                        render: (text) => (
                            <span className=''>{text ?? '-'}</span>
                        )
                    },
                    {
                        title: 'Document Type',
                        dataIndex: 'documentType',
                        key: 'documentType',
                        render: (text) => (
                            <span className='capitalize'>
                                {text.toLowerCase().split('_').join(' ')}
                            </span>
                        )
                    },
                    {
                        title: 'Data Type',
                        dataIndex: 'createdType',
                        key: 'createdType',
                        render: (text) => (
                            <span>{text === 'UPLOADED' ? 'File' : 'Form'}</span>
                        )
                    },
                    {
                        title: '',
                        render: (_, data) => (
                            <div
                                className='cursor-pointer'
                                onClick={() => {
                                    props.setCurrentData(data);
                                }}
                            >
                                <Dropdown
                                    menu={{ items }}
                                    trigger={['click']}
                                    placement='bottomRight'
                                >
                                    <img
                                        src='/img/vectors/more.svg'
                                        alt='more'
                                    />
                                </Dropdown>
                            </div>
                        )
                    }
                ]}
                mobileComponent={
                    <DocumentsTableMobileCard
                        menuItems={items}
                        setCurrentData={props.setCurrentData}
                    />
                }
            />
            {singleData && !downloadLoading && (
                <MainPreviewComponent
                    data={singleData}
                    currentData={props.currentData}
                    ref={printContentRef}
                />
            )}
        </div>
    );
};

export default AllDocumentsTable;
